/* eslint-disable no-console */
import React, { Fragment, useCallback, useMemo, useState } from "react";

import { useApolloClient, useMutation } from "@apollo/client";
import { FormikProvider, useFormik } from "formik";
import { isEmpty, merge } from "lodash";
import { toast } from "react-toastify";
import { Form } from "reactstrap";
import * as yup from "yup";

import UPDATE_CLIENT from "graphql/mutations/clients/updateClient";
import ALL_COURTS from "graphql/sanity/allCourts";

import AutoSave from "pages/profile/AutoSave";

import difference from "components/calculations/utils/difference";
import Button from "components/common/Button";
import CourtLocationSelect from "components/common/inputs/Select/CourtLocationSelect";
import CourtSelect from "components/common/inputs/Select/CourtSelect";
import ProvinceSelect from "components/common/inputs/Select/ProvinceSelect";
import TextInput from "components/common/inputs/TextInput";

import useCourtInformation from "hooks/me/useCourtInformation";

import assignObjectValuesToSet from "utils/assignObjectValuesToSet";

const isCourtInfoOptional = (fileType) => {
  return ["ADR", "ACTING_AS_MEDIATOR"].includes(fileType);
};

const getValidationSchema = (fileType, showCourtInfo) => {
  const isOptional = isCourtInfoOptional(fileType);

  if (isOptional && !showCourtInfo) {
    // When court info is optional and toggle is off, no validation required
    return yup.object().shape({});
  }

  // Standard validation for required court info
  return yup.object().shape({
    provinceShorthand: yup.string().required("Province is required"),
    courtId: yup.string().required("Court is required"),
    courtFile: yup.string().required("Court File is required"),
  });
};

const CourtForm = ({ user, hideFields = [], onSubmit }) => {
  const [updateClient, { loading }] = useMutation(UPDATE_CLIENT);
  const apollo = useApolloClient();

  const fileType = user?.client?.fileType;
  const isOptional = isCourtInfoOptional(fileType);

  const [showCourtInfo, setShowCourtInfo] = useState(() => {
    if (!isOptional) return true;

    // For optional file types, show if there's existing court data
    const existingCourtData = user?.client?.court;
    return !!(
      existingCourtData?.courtFile ||
      existingCourtData?.courtId ||
      existingCourtData?.provinceShorthand
    );
  });

  const {
    courtFile: defaultCourtFile,
    courtId: defaultCourtId,
    courtLocationId: defaultCourtLocationId,
    provinceShorthand: defaultResidence,
  } = useCourtInformation(user?.client);

  const initialValues = useMemo(
    () => ({
      provinceShorthand: defaultResidence || "",
      courtFile: defaultCourtFile || "",
      courtId: defaultCourtId || "",
      courtLocationId: defaultCourtLocationId || "",
    }),
    [defaultCourtFile, defaultCourtId, defaultCourtLocationId, defaultResidence],
  );

  const validationSchema = useMemo(
    () => getValidationSchema(fileType, showCourtInfo),
    [fileType, showCourtInfo],
  );

  const handleSubmitForm = useCallback(
    (values, { resetForm }) => {
      const differenceData = difference(values, initialValues);

      try {
        if (!isEmpty(differenceData)) {
          const cachedData = apollo.readQuery({
            query: ALL_COURTS,
            variables: { where: { province: { shorthand: { eq: values?.provinceShorthand } } } },
          });

          const locationData = cachedData?.allCourt
            .find((court) => court._id === values?.courtId)
            ?.locations.find((location) => location._id === values?.courtLocationId);

          if (locationData) {
            merge(values, {
              street1: locationData.street1,
              street2: locationData.street2,
              postal: locationData.postal,
              city: locationData.city?.name,
              province: locationData.province?.name,
              judicialDistrict: locationData.judicialDistrict?.name,
            });
          }

          const variables = {
            data: {
              court: {
                upsert: {
                  create: values,
                  update: assignObjectValuesToSet(values),
                },
              },
            },
            where: {
              id: user?.client?.id,
            },
          };

          updateClient({ variables }).then(() => {
            toast.success("Court information auto-saved", { autoFocus: false });
            resetForm({ values });
          });
        }
      } catch (err) {
        console.error(err);
        err.graphQLErrors.forEach(({ message }) => toast.error(message), { autoFocus: false });
      } finally {
        onSubmit?.(values);
      }
    },
    [initialValues, apollo, user?.client?.id, updateClient, onSubmit],
  );

  const formik = useFormik({
    initialValues,
    onSubmit: handleSubmitForm,
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema,
  });

  const { setFieldValue, values, handleSubmit } = formik;

  const disabled = loading;

  const handleToggleCourtInfo = useCallback(() => {
    if (showCourtInfo) {
      const clearValues = {
        provinceShorthand: "",
        courtFile: "",
        courtId: "",
        courtLocationId: "",
      };

      setShowCourtInfo(false);

      formik.setValues(clearValues);

      const hasCourtData = user?.client?.court;

      if (hasCourtData) {
        const variables = {
          data: {
            court: {
              delete: {
                clientId: {
                  equals: user?.client?.id,
                },
              },
            },
          },
          where: {
            id: user?.client?.id,
          },
        };

        updateClient({
          variables,
          update: (cache, { data }) => {
            if (data?.updateClient) {
              cache.modify({
                id: cache.identify(data.updateClient),
                fields: {
                  court: () => null,
                },
              });
            }
          },
        })
          .then(() => {
            toast.success("Court information removed", { autoFocus: false });
          })
          .catch((err) => {
            console.error(err);
            err.graphQLErrors?.forEach(({ message }) => toast.error(message));
            // Revert UI state if API call fails
            setShowCourtInfo(true);
          });
      } else {
        toast.success("Court information cleared", { autoFocus: false });
      }
    } else {
      setShowCourtInfo(true);
    }
  }, [showCourtInfo, formik, updateClient, user?.client?.id, user?.client?.court]);

  return (
    <FormikProvider value={formik}>
      {!onSubmit && <AutoSave />}
      <Form className="court-form" onSubmit={handleSubmit}>
        {/* Show optional court info message and toggle for ADR file types */}
        {isOptional && (
          <div className="mb-3 d-flex justify-content-end">
            <Button
              type="button"
              color={showCourtInfo ? "outline-secondary" : "outline-primary"}
              size="sm"
              onClick={handleToggleCourtInfo}
              leftFAIcon={showCourtInfo ? "minus" : "plus"}
            >
              {showCourtInfo ? "Remove Court Info" : "Add Court Info"}
            </Button>
          </div>
        )}

        {/* Court information fields - shown based on file type and toggle state */}
        {(!isOptional || showCourtInfo) && (
          <Fragment>
            {!hideFields.includes("provinceShorthand") && (
              <ProvinceSelect
                isRequired={!isOptional || showCourtInfo}
                isFormikField
                label="Province"
                disabled={disabled}
                containerClassName="mb-3"
                onChange={({ value }) => {
                  setFieldValue("provinceShorthand", value);
                  setFieldValue("courtId", "");
                  setFieldValue("courtLocationId", "");
                }}
              />
            )}

            {(!hideFields.includes("courtId") || !values.courtId) && (
              <CourtSelect
                label="Select Court"
                containerClassName="mb-3"
                provinceShorthand={values?.provinceShorthand}
                disabled={!values?.provinceShorthand || disabled}
                onChange={({ value }) => {
                  setFieldValue("courtId", value);
                  setFieldValue("courtLocationId", "");
                }}
                isFormikField
                isRequired={!isOptional || showCourtInfo}
              />
            )}

            {!hideFields.includes("courtLocationId") && (
              <CourtLocationSelect
                label="Court Location"
                containerClassName="mb-3"
                courtId={values?.courtId}
                provinceShorthand={values?.provinceShorthand}
                disabled={!values?.courtId || disabled}
                isFormikField
              />
            )}

            {!hideFields.includes("courtFile") && (
              <TextInput
                name="courtFile"
                label="Court File Number"
                placeholder="Court File Number"
                containerClassName="mb-4"
                hint="If this is a new court action, enter a blank space as a placeholder"
                disabled={disabled}
                isRequired={!isOptional || showCourtInfo}
              />
            )}
          </Fragment>
        )}

        {onSubmit && (
          <div className="d-flex justify-content-between align-items-end buttons">
            <Button type="submit" size="lg" leftFAIcon="check" disabled={disabled}>
              Save & Submit
            </Button>
          </div>
        )}
      </Form>
    </FormikProvider>
  );
};

export default CourtForm;
